import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MdSearch,
  MdChevronLeft,
  MdChevronRight,
  MdSettings,
  MdExpandMore,
  MdCheck,
} from 'react-icons/md';
import examAPI from '../../api/examAPI';
import { useEmployeeAttendanceAuth } from '../../context/EmployeeAttendanceAuthContext';
import { useEmployeeAttendanceHrAuth } from '../../context/EmployeeAttendanceHrAuthContext';
import { fetchWithHrToken, HR_SESSION_ERROR } from '../../utils/hrApiHelpers';
import {
  readSavedListFilters,
  writeSavedListFilters,
} from '../../utils/admissionsListFilters';
import '../../employeeAttendance.css';
import '../../employeeAttendance-hr.css';
import '../../employeeAttendance-admissions.css';

const PAGE_SIZE = 80;
const FILTER_KEY = 'exam-entries';

const SEMESTERS = [
  { value: '1', label: 'Semester I' },
  { value: '2', label: 'Semester II' },
  { value: '3', label: 'Semester III' },
  { value: '4', label: 'Semester IV' },
  { value: '5', label: 'Semester V' },
  { value: '6', label: 'Semester VI' },
  { value: '7', label: 'Semester VII' },
  { value: '8', label: 'Semester VIII' },
  { value: '9', label: 'Semester IX' },
  { value: '10', label: 'Semester X' },
  { value: '11', label: 'Semester XI' },
  { value: '12', label: 'Semester XII' },
];

const DATE_GRAINS = [
  { id: 'year', label: 'Year' },
  { id: 'quarter', label: 'Quarter' },
  { id: 'month', label: 'Month' },
  { id: 'week', label: 'Week' },
  { id: 'day', label: 'Day' },
];

const DATE_GROUP_FIELDS = {
  created_on: 'create_date',
  write_date: 'write_date',
};

const MENU_FIELDS = [
  { id: 'academic_year', label: 'Academic Year', kind: 'lookup' },
  { id: 'create_uid', label: 'Created by', kind: 'group', groupField: 'create_uid' },
  { id: 'created_on', label: 'Created on', kind: 'date' },
  { id: 'write_uid', label: 'Last Updated by', kind: 'group', groupField: 'write_uid' },
  { id: 'write_date', label: 'Last Updated on', kind: 'date' },
];

function optionId(opt) {
  return String(opt?.id ?? opt?.value ?? '');
}

function optionName(opt) {
  return opt?.name || opt?.label || opt?.code || '';
}

const GRAIN_ORDER = ['year', 'quarter', 'month', 'week', 'day'];
const DEFAULT_DATE_GRAINS = ['year', 'week', 'day'];

function orderedGrains(selected) {
  return GRAIN_ORDER.filter((grain) => selected.includes(grain));
}

function RecordRow({ row, onOpen, indent = 0 }) {
  return (
    <tr className="ea-odoo-att-list__row" onClick={() => onOpen(row.id)}>
      <td className="ea-exam-list__check" onClick={(e) => e.stopPropagation()}>
        <input type="checkbox" aria-label={`Select ${row.subject_name || row.id}`} />
      </td>
      <td style={indent ? { paddingLeft: 24 + indent * 18 } : undefined}>
        <span className="ea-exam-link">{row.faculty_name || ''}</span>
      </td>
      <td>
        <span className="ea-exam-link">{row.department_name || ''}</span>
      </td>
      <td>
        <span className="ea-exam-link">{row.batch_name || ''}</span>
      </td>
      <td>
        <span className="ea-exam-link">{row.semester_label || row.semester || ''}</span>
      </td>
      <td>
        <span className="ea-exam-link ea-exam-link--subject">{row.subject_name || ''}</span>
      </td>
    </tr>
  );
}

export default function ExamEntriesPage() {
  const navigate = useNavigate();
  const { ensureHrToken } = useEmployeeAttendanceHrAuth();
  const { user } = useEmployeeAttendanceAuth();
  const saved = readSavedListFilters(FILTER_KEY) || {};
  const menuRef = useRef(null);

  const [records, setRecords] = useState([]);
  const [groups, setGroups] = useState([]);
  const [grouped, setGrouped] = useState(false);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [searchInput, setSearchInput] = useState(saved.search || '');
  const [search, setSearch] = useState(saved.search || '');
  const [facultyId, setFacultyId] = useState(saved.facultyId || '');
  const [departmentId, setDepartmentId] = useState(saved.departmentId || '');
  const [batchId, setBatchId] = useState(saved.batchId || '');
  const [semester, setSemester] = useState(saved.semester || '');
  const [subjectId, setSubjectId] = useState(saved.subjectId || '');
  const [academicYearId, setAcademicYearId] = useState(saved.academicYearId || '');
  const [dateGroupField, setDateGroupField] = useState(saved.dateGroupField || '');
  const [dateGrains, setDateGrains] = useState(saved.dateGrains || []);
  const [groupByField, setGroupByField] = useState(saved.groupByField || '');
  const [, setGroupDomain] = useState(null);
  const [, setGroupLabel] = useState('');
  const [faculties, setFaculties] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [batches, setBatches] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeField, setActiveField] = useState('academic_year');
  const [valueQuery, setValueQuery] = useState('');
  const [expanded, setExpanded] = useState({});
  const [treeNodes, setTreeNodes] = useState({});

  const groupLevels = useMemo(() => {
    if (dateGroupField && dateGrains.length) {
      const field = DATE_GROUP_FIELDS[dateGroupField];
      return orderedGrains(dateGrains).map((grain) => `${field}:${grain}`);
    }
    if (groupByField) return [groupByField];
    return [];
  }, [dateGroupField, dateGrains, groupByField]);

  const groupBy = groupLevels[0] || '';

  const filterParams = useMemo(
    () => ({
      search: search || undefined,
      faculty_id: facultyId ? Number(facultyId) : undefined,
      department_id: departmentId ? Number(departmentId) : undefined,
      batch_id: batchId ? Number(batchId) : undefined,
      semester: semester || undefined,
      subject_id: subjectId ? Number(subjectId) : undefined,
      academic_year_id: academicYearId ? Number(academicYearId) : undefined,
    }),
    [search, facultyId, departmentId, batchId, semester, subjectId, academicYearId]
  );

  const openEntry = (id) => {
    navigate(`/bu-dashboard/exams/entries/${id}`);
  };

  const loadLookups = useCallback(async () => {
    const facRes = await fetchWithHrToken(ensureHrToken, (token) => examAPI.getFaculties(token));
    if (facRes.error === 'no_token') {
      setError(user?.hr_api_error || HR_SESSION_ERROR);
      return;
    }
    if (!facRes.error) setFaculties(facRes.data?.faculties || facRes.data?.records || []);
    const yearRes = await fetchWithHrToken(ensureHrToken, (token) => examAPI.getAcademicYears(token));
    if (!yearRes.error) {
      setAcademicYears(yearRes.data?.academic_years || yearRes.data?.records || []);
    }
  }, [ensureHrToken, user?.hr_api_error]);

  useEffect(() => {
    loadLookups();
  }, [loadLookups]);

  useEffect(() => {
    if (!facultyId) {
      setDepartments([]);
      return;
    }
    fetchWithHrToken(ensureHrToken, (token) => examAPI.getDepartments(facultyId, token)).then(
      ({ data }) => setDepartments(data?.departments || data?.records || [])
    );
  }, [ensureHrToken, facultyId]);

  useEffect(() => {
    if (!departmentId) {
      setBatches([]);
      return;
    }
    fetchWithHrToken(ensureHrToken, (token) => examAPI.getBatches(departmentId, token)).then(
      ({ data }) => setBatches(data?.batches || data?.records || [])
    );
  }, [ensureHrToken, departmentId]);

  useEffect(() => {
    if (!departmentId) {
      setSubjects([]);
      return;
    }
    fetchWithHrToken(ensureHrToken, (token) =>
      examAPI.getSubjects(
        {
          faculty_id: facultyId ? Number(facultyId) : undefined,
          department_id: Number(departmentId),
          semester: semester || undefined,
          batch_id: batchId ? Number(batchId) : undefined,
        },
        token
      )
    ).then(({ data }) => setSubjects(data?.subjects || data?.records || []));
  }, [ensureHrToken, facultyId, departmentId, semester, batchId]);

  const loadEntries = useCallback(async () => {
    setLoading(true);
    setError(null);
    setExpanded({});
    setTreeNodes({});
    const { data, error: fetchError } = await fetchWithHrToken(ensureHrToken, (token) =>
      examAPI.getEntries(
        {
          ...filterParams,
          group_by: groupBy || undefined,
          limit: PAGE_SIZE,
          offset: groupBy ? 0 : offset,
        },
        token
      )
    );
    if (fetchError === 'no_token') {
      setError(user?.hr_api_error || HR_SESSION_ERROR);
      setRecords([]);
      setGroups([]);
      setTotal(0);
      setLoading(false);
      return;
    }
    if (fetchError) {
      setError(fetchError);
      setRecords([]);
      setGroups([]);
      setTotal(0);
      setLoading(false);
      return;
    }
    setGrouped(Boolean(data.grouped));
    setGroups(data.groups || []);
    setRecords(data.records || []);
    setTotal(Number(data.total) || 0);
    setLoading(false);
  }, [ensureHrToken, filterParams, groupBy, offset, user?.hr_api_error]);

  useEffect(() => {
    loadEntries();
  }, [loadEntries]);

  useEffect(() => {
    writeSavedListFilters(FILTER_KEY, {
      search,
      facultyId,
      departmentId,
      batchId,
      semester,
      subjectId,
      academicYearId,
      dateGroupField,
      dateGrains,
      groupByField,
    });
  }, [
    search,
    facultyId,
    departmentId,
    batchId,
    semester,
    subjectId,
    academicYearId,
    dateGroupField,
    dateGrains,
    groupByField,
  ]);

  useEffect(() => {
    const t = setTimeout(() => {
      setOffset(0);
      setSearch(searchInput.trim());
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    const onDoc = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const resetOffset = () => {
    setOffset(0);
    setGroupDomain(null);
    setGroupLabel('');
  };

  const applyFaculty = (id) => {
    setFacultyId(id);
    setDepartmentId('');
    setBatchId('');
    setSubjectId('');
    resetOffset();
  };

  const applyDepartment = (id) => {
    setDepartmentId(id);
    setBatchId('');
    setSubjectId('');
    resetOffset();
  };

  const applyBatch = (id) => {
    setBatchId(id);
    resetOffset();
  };

  const activeMeta = MENU_FIELDS.find((f) => f.id === activeField) || MENU_FIELDS[0];
  const lookupOptions = useMemo(() => {
    const rows = activeField === 'academic_year' ? academicYears : [];
    const q = valueQuery.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) => optionName(row).toLowerCase().includes(q));
  }, [activeField, academicYears, valueQuery]);

  const pickLookup = (opt) => {
    if (activeField === 'academic_year') {
      const id = optionId(opt);
      setAcademicYearId((prev) => (prev === id ? '' : id));
      resetOffset();
    }
    setValueQuery('');
  };

  const applyDateGroup = (fieldId, grains) => {
    setGroupByField('');
    setDateGroupField(fieldId);
    setDateGrains(grains);
    resetOffset();
  };

  const clearDateGroup = () => {
    setDateGroupField('');
    setDateGrains([]);
    setGroupByField('');
    resetOffset();
  };

  const toggleDateGrain = (grainId) => {
    setGroupByField('');
    const current = dateGroupField === activeField ? dateGrains : [];
    const next = current.includes(grainId)
      ? current.filter((grain) => grain !== grainId)
      : [...current, grainId];
    if (!next.length) {
      setDateGroupField('');
      setDateGrains([]);
    } else {
      setDateGroupField(activeField);
      setDateGrains(next);
    }
    resetOffset();
  };

  const toggleNode = async (path, depth, domain) => {
    if (expanded[path]) {
      setExpanded((prev) => ({ ...prev, [path]: false }));
      return;
    }
    setExpanded((prev) => ({ ...prev, [path]: true }));
    if (treeNodes[path] && !treeNodes[path].loading) return;
    setTreeNodes((prev) => ({ ...prev, [path]: { loading: true, groups: [], records: [] } }));
    const nextLevel = groupLevels[depth + 1];
    const { data, error: fetchError } = await fetchWithHrToken(ensureHrToken, (token) =>
      examAPI.getEntries(
        {
          ...filterParams,
          group_by: nextLevel || undefined,
          group_domain: domain || undefined,
          limit: nextLevel ? 500 : 500,
          offset: 0,
        },
        token
      )
    );
    if (fetchError) {
      setTreeNodes((prev) => ({
        ...prev,
        [path]: { loading: false, groups: [], records: [], error: fetchError },
      }));
      return;
    }
    setTreeNodes((prev) => ({
      ...prev,
      [path]: {
        loading: false,
        groups: data.grouped ? data.groups || [] : [],
        records: data.grouped ? [] : data.records || [],
      },
    }));
  };

  const renderGroupRows = (items, depth, parentPath) =>
    items.map((group, idx) => {
      const path = parentPath ? `${parentPath}/${idx}` : String(idx);
      const isOpen = Boolean(expanded[path]);
      const node = treeNodes[path];
      return (
        <React.Fragment key={path}>
          <tr className="ea-exam-group-row" onClick={() => toggleNode(path, depth, group.domain || [])}>
            <td colSpan={6} style={{ paddingLeft: 12 + depth * 18 }}>
              <span className={`ea-exam-tree__caret${isOpen ? ' is-open' : ''}`} aria-hidden />
              <strong>{group.label || 'Undefined'}</strong>
              <span className="ea-exam-group-count">({group.count})</span>
            </td>
          </tr>
          {isOpen && node?.loading ? (
            <tr>
              <td colSpan={6} className="ea-hr-table__empty" style={{ paddingLeft: 30 + depth * 18 }}>
                Loading…
              </td>
            </tr>
          ) : null}
          {isOpen && node?.groups?.length
            ? renderGroupRows(node.groups, depth + 1, path)
            : null}
          {isOpen && node?.records?.length
            ? node.records.map((row) => (
                <RecordRow key={row.id} row={row} onOpen={openEntry} indent={depth + 1} />
              ))
            : null}
        </React.Fragment>
      );
    });

  const rangeStart = total === 0 ? 0 : offset + 1;
  const rangeEnd = grouped ? total : Math.min(offset + PAGE_SIZE, total);
  const canPrev = !grouped && offset > 0;
  const canNext = !grouped && offset + PAGE_SIZE < total;

  return (
    <div className="ea-hr-dash ea-odoo-emp ea-adm ea-exam">
      <div className="ea-odoo-control">
        <div className="ea-odoo-control__left">
          <button
            type="button"
            className="ea-odoo-new"
            onClick={() => navigate('/bu-dashboard/exams/entries/new')}
          >
            New
          </button>
          <div className="ea-odoo-control__title">
            Multi Exam Entry
            <button type="button" className="ea-odoo-icon-btn" title="View settings" aria-label="View settings">
              <MdSettings size={16} />
            </button>
          </div>
        </div>

        <div className="ea-exam-search" ref={menuRef}>
          <MdSearch className="ea-exam-search__icon" aria-hidden />
          <input
            type="search"
            placeholder="Search..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            aria-label="Search exam entries"
          />
          <span className="ea-exam-search__split" aria-hidden />
          <button
            type="button"
            className="ea-exam-search__toggle"
            aria-label="Open search filters"
            onClick={() => setMenuOpen((open) => !open)}
          >
            <MdExpandMore size={18} />
          </button>

          {menuOpen && (
            <div className="ea-exam-menu">
              <div className="ea-exam-menu__fields">
                {MENU_FIELDS.map((field) => (
                  <button
                    key={field.id}
                    type="button"
                    className={`ea-exam-menu__field${activeField === field.id ? ' is-active' : ''}`}
                    onMouseEnter={() => {
                      setActiveField(field.id);
                      setValueQuery('');
                    }}
                    onClick={() => {
                      setActiveField(field.id);
                      setValueQuery('');
                      if (field.kind === 'group') {
                        setDateGroupField('');
                        setDateGrains([]);
                        setGroupByField((prev) =>
                          prev === field.groupField ? '' : field.groupField
                        );
                        resetOffset();
                      }
                      if (field.kind === 'date') {
                        if (dateGroupField === field.id) {
                          clearDateGroup();
                        } else {
                          applyDateGroup(field.id, DEFAULT_DATE_GRAINS);
                        }
                      }
                    }}
                  >
                    {(field.kind === 'group' && groupByField === field.groupField) ||
                    (field.kind === 'date' && dateGroupField === field.id) ? (
                      <MdCheck className="ea-exam-menu__check" size={16} />
                    ) : (
                      <span className="ea-exam-menu__check-slot" />
                    )}
                    <span>{field.label}</span>
                    {(field.kind === 'date' || field.kind === 'lookup') && (
                      <MdExpandMore className="ea-exam-menu__caret" size={16} />
                    )}
                  </button>
                ))}
              </div>
              <div className="ea-exam-menu__values">
                {activeMeta.kind === 'date' && (
                  <>
                    <div className="ea-exam-menu__values-title">{activeMeta.label}</div>
                    {DATE_GRAINS.map((grain) => {
                      const checked = dateGroupField === activeField && dateGrains.includes(grain.id);
                      return (
                        <button
                          key={grain.id}
                          type="button"
                          className={`ea-exam-menu__value${checked ? ' is-checked' : ''}`}
                          onClick={() => toggleDateGrain(grain.id)}
                        >
                          {checked ? <MdCheck size={16} /> : <span className="ea-exam-menu__check-slot" />}
                          {grain.label}
                        </button>
                      );
                    })}
                  </>
                )}
                {activeMeta.kind === 'lookup' && (
                  <>
                    <div className="ea-exam-menu__values-title">{activeMeta.label}</div>
                    <input
                      type="search"
                      className="ea-exam-menu__search"
                      placeholder={`Search ${activeMeta.label.toLowerCase()}...`}
                      value={valueQuery}
                      onChange={(e) => setValueQuery(e.target.value)}
                    />
                    {lookupOptions.length === 0 ? (
                      <div className="ea-exam-menu__empty">No matching results</div>
                    ) : (
                      lookupOptions.slice(0, 80).map((opt) => (
                        <button
                          key={optionId(opt)}
                          type="button"
                          className="ea-exam-menu__value"
                          onClick={() => pickLookup(opt)}
                        >
                          {optionName(opt)}
                        </button>
                      ))
                    )}
                  </>
                )}
                {activeMeta.kind === 'group' && (
                  <div className="ea-exam-menu__empty">Click the field to group the list.</div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="ea-odoo-control__right">
          <div className="ea-odoo-pager" aria-label="Pagination">
            <span>{loading ? '…' : `${rangeStart}-${rangeEnd} / ${total}`}</span>
            <button
              type="button"
              className="ea-odoo-pager__btn"
              disabled={!canPrev || loading}
              onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
              aria-label="Previous page"
            >
              <MdChevronLeft size={20} />
            </button>
            <button
              type="button"
              className="ea-odoo-pager__btn"
              disabled={!canNext || loading}
              onClick={() => setOffset((o) => o + PAGE_SIZE)}
              aria-label="Next page"
            >
              <MdChevronRight size={20} />
            </button>
          </div>
        </div>
      </div>

      <div className="ea-odoo-filters ea-exam-filters">
        <label>
          <span className="ea-field-label">Faculty</span>
          <select
            className="ea-odoo-filters__select"
            value={facultyId}
            onChange={(e) => applyFaculty(e.target.value)}
          >
            <option value="">All faculties</option>
            {faculties.map((f) => (
              <option key={f.id} value={f.id}>
                {f.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span className="ea-field-label">Department</span>
          <select
            className="ea-odoo-filters__select"
            value={departmentId}
            disabled={!facultyId}
            onChange={(e) => applyDepartment(e.target.value)}
          >
            <option value="">{facultyId ? 'All departments' : 'Select faculty first'}</option>
            {departments.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span className="ea-field-label">Batch</span>
          <select
            className="ea-odoo-filters__select"
            value={batchId}
            disabled={!departmentId}
            onChange={(e) => applyBatch(e.target.value)}
          >
            <option value="">{departmentId ? 'All batches' : 'Select department first'}</option>
            {batches.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span className="ea-field-label">Semester</span>
          <select
            className="ea-odoo-filters__select"
            value={semester}
            onChange={(e) => {
              setSemester(e.target.value);
              setSubjectId('');
              resetOffset();
            }}
          >
            <option value="">All semesters</option>
            {SEMESTERS.map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span className="ea-field-label">Subject</span>
          <select
            className="ea-odoo-filters__select"
            value={subjectId}
            disabled={!departmentId}
            onChange={(e) => {
              setSubjectId(e.target.value);
              resetOffset();
            }}
          >
            <option value="">{departmentId ? 'All subjects' : 'Select department first'}</option>
            {subjects.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </label>
      </div>

      {error && (
        <div className="ea-toast error show ea-dash-error" role="alert">
          {error}
        </div>
      )}

      <div className="ea-odoo-list-wrap">
        <table className="ea-odoo-list ea-exam-list">
          <thead>
            <tr>
              <th className="ea-exam-list__check">
                <input type="checkbox" disabled aria-label="Select all" />
              </th>
              <th>Faculty</th>
              <th>Department</th>
              <th>Batch</th>
              <th>Semester</th>
              <th>Subject</th>
            </tr>
          </thead>
          <tbody>
            {loading && records.length === 0 && groups.length === 0 ? (
              <tr>
                <td colSpan={6} className="ea-hr-table__empty">
                  Loading exam entries…
                </td>
              </tr>
            ) : grouped ? (
              groups.length === 0 ? (
                <tr>
                  <td colSpan={6} className="ea-hr-table__empty">
                    No exam entries found.
                  </td>
                </tr>
              ) : (
                renderGroupRows(groups, 0, '')
              )
            ) : records.length === 0 ? (
              <tr>
                <td colSpan={6} className="ea-hr-table__empty">
                  No exam entries found.
                </td>
              </tr>
            ) : (
              records.map((row) => <RecordRow key={row.id} row={row} onOpen={openEntry} />)
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
